// src/components/Feed/TagCustomersPicker.js
// @flow

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';
import { useTheme } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import SaveIcon from '@mui/icons-material/Save';
import invert from 'invert-color';
import { log } from '../../utils/jsUtils';
import { useAddTagToCustomerMutation, useCreateTagMutation } from '../../features/api/tagsApi';
import { UPDATE_REQUESTS } from '../../reduxStore/modules/feed/types';
import { ADD_TAG, DELETE_TAG } from '../../reduxStore/modules/tags/types';
import { selectAllTags, selectFeedRequests } from '../../reduxStore/modules/tags/selectors';
import { selectUser } from '../../reduxStore/modules/user/selectors';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import TagColorPicker from '../SharedComponents/TagColorPicker';
import type { TagType } from '../../types/redux';
// createTag is now handled via useCreateTagMutation from tagsApi
import { TwitterPicker } from 'react-color';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';

type NewTagType = {
  Name: string,
  Color: string
};

type Props = {
  customerId: string,
  customerName?: string,
  existingTags?: Array<TagType>,
  onClose: Function,
  onTagPicked?: Function,
  debug?: boolean,
  anchorEl: HTMLElement | null
};

type NewTagInputProps = {
  newTag: NewTagType,
  setNewTag: (tag: NewTagType) => void,
  onSubmit: () => void,
  onColorPicked: (color: Object) => void,
  debug?: boolean
};

// Custom hook for managing input focus
const useFocus = () => {
  const inputRef = useRef(null);
  const setFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  return [inputRef, setFocus];
};

// Component for adding a new tag
const NewTagInput = ({ newTag, setNewTag, onSubmit, onColorPicked, debug = false }: NewTagInputProps) => {
  const theme = useTheme();
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [hexInputValue, setHexInputValue] = useState('');
  const [inputRef, setInputFocus] = useFocus();
  const colorSwatchRef = useRef(null);
  
  const handleNameChange = (e) => {
    setNewTag({
      ...newTag,
      Name: e.target.value
    });
  };
  
  const handleColorClick = () => {
    setDisplayColorPicker(!displayColorPicker);
  };
  
  const handleColorClose = () => {
    setDisplayColorPicker(false);
    // Focus back on input when color picker closes
    setTimeout(() => {
      setInputFocus();
    }, 100);
  };
  
  const handleColorChange = (color) => {
    // Sync hexInputValue when swatch is clicked
    const hexValue = color.hex.replace('#', '').toUpperCase();
    setHexInputValue(hexValue);
    setNewTag({
      ...newTag,
      Color: color.hex
    });
  };

  // Handler for custom hex input - prevents auto-completion
  const handleHexInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    // Only allow hex characters (0-9, A-F)
    const filteredValue = value.replace(/[^0-9A-F]/g, '').slice(0, 6);
    setHexInputValue(filteredValue);

    // Only update the actual color when we have a complete 6-digit hex
    if (filteredValue.length === 6) {
      setNewTag({
        ...newTag,
        Color: '#' + filteredValue
      });
    }
  };

  // Compute preview color for the hex input
  const getPreviewColor = () => {
    if (hexInputValue.length === 6) {
      return '#' + hexInputValue;
    }
    // Pad incomplete hex with zeros for preview
    if (hexInputValue.length > 0) {
      return '#' + hexInputValue.padEnd(6, '0');
    }
    return newTag.Color;
  };

  const handleColorPickerKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleColorClose();
    }
  };
  
  const handleKeyPress = (e) => {
    e.stopPropagation();
    if (debug) log('[TagCustomersPicker] handleKeyPress');
    if (e.key === 'Enter') {
      if (debug) log('[NewTagInput] saving new tag: ', newTag);
      onSubmit();
    }
  };
  
  const handleKeyDown = (e) => {
    e.stopPropagation();
    // This prevents event bubbling
  };

  
  return (
    <Box sx={{ width: '100%' }}>
      <OutlinedInput
        autoFocus
        fullWidth
        placeholder="New tag name"
        value={newTag.Name}
        onChange={handleNameChange}
        onKeyDown={handleKeyDown}
        onKeyPress={handleKeyPress}
        inputRef={inputRef}
        size="small"
        startAdornment={
          <InputAdornment position="start">
            <Box
              ref={colorSwatchRef}
              onClick={handleColorClick}
              sx={{
                backgroundColor: newTag.Color,
                width: '24px',
                height: '24px',
                cursor: 'pointer',
                borderRadius: '4px',
                border: '1px solid rgba(0,0,0,0.1)',
                mr: 1
              }}
            />
          </InputAdornment>
        }
        endAdornment={
          newTag.Name !== '' ? (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                color="primary"
                onClick={onSubmit}
                size="small"
              >
                <SaveIcon />
              </IconButton>
            </InputAdornment>
          ) : null
        }
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.87)',
          }
        }}
      />
      
      <Popper
        open={displayColorPicker}
        anchorEl={colorSwatchRef.current}
        placement="bottom-start"
        style={{ zIndex: theme.zIndex.modal + 10 }}
      >
        <ClickAwayListener
          onClickAway={handleColorClose}
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
        >
          <Box
            onKeyDown={handleColorPickerKeyDown}
            sx={{
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}
          >
            <TwitterPicker
              color={newTag.Color}
              onChange={handleColorChange}
              triangle="hide"
              colors={[
                '#FF6900', '#FCB900', '#7BDCB5', '#00D084', '#8ED1FC',
                '#0693E3', '#ABB8C3', '#EB144C', '#F78DA7', '#9900EF',
                '#CCCCCC', '#333333'
              ]}
              styles={{
                default: {
                  input: { display: 'none' },
                  hash: { display: 'none' },
                  card: {
                    boxShadow: 'none',
                    borderRadius: '4px 4px 0 0'
                  }
                }
              }}
            />
            {/* Custom hex input with color preview */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              py: 1,
              backgroundColor: '#fff',
              borderTop: '1px solid #e0e0e0',
              borderRadius: '0 0 4px 4px'
            }}>
              <Box
                sx={{
                  backgroundColor: getPreviewColor(),
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  mr: 1,
                  flexShrink: 0
                }}
              />
              <Box sx={{ color: '#999', mr: 0.5, fontSize: '14px' }}>#</Box>
              <OutlinedInput
                value={hexInputValue}
                onChange={handleHexInputChange}
                placeholder="FFFFFF"
                size="small"
                inputProps={{
                  maxLength: 6,
                  style: {
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    padding: '4px 8px',
                    width: '70px'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  }
                }}
              />
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

const TagCustomersPicker = ({ 
  customerId, 
  customerName = '', 
  existingTags = [], 
  onClose, 
  onTagPicked, 
  debug = false, 
  anchorEl 
}: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(Boolean(anchorEl));
  const [newTag, setNewTag] = useState<NewTagType>({
    Name: '',
    Color: '#CCCCCC'
  });
  
  // Use memoized selectors from the Redux store
  const allTags = useSelector(selectAllTags);
  const user = useSelector(selectUser);
  const requests = useSelector(selectFeedRequests);
  
  const [addTagToCustomer] = useAddTagToCustomerMutation();
  const [createTagMutation] = useCreateTagMutation();

  // Handle menu close
  const handleClose = useCallback((event) => {
    // Stop propagation to prevent any parent handlers from being called
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    setOpen(false);
    if (onClose) onClose();
  }, [onClose]);
  
  // Effect to sync open state with anchorEl
  useEffect(() => {
    const newOpenState = Boolean(anchorEl);
    setOpen(newOpenState);
  }, [anchorEl]);
  
  // Ensure proper scroll position when menu opens
  useEffect(() => {
    if (open && anchorEl) {
      // Small delay to ensure the menu is rendered before scrolling
      setTimeout(() => {
        const menuElement = document.getElementById('tag-picker-menu');
        if (menuElement) {
          menuElement.scrollTop = 0;
        }
      }, 50);
    }
  }, [open, anchorEl]);
  
  // Log component mounting in debug mode
  useEffect(() => {
    if (debug) {
      log('[TagCustomersPicker] Props:', {
        customerId,
        hasOnClose: typeof onClose === 'function',
        hasOnTagPicked: typeof onTagPicked === 'function',
        existingTagsCount: existingTags?.length
      });
      
      log('[TagCustomersPicker] Redux State:', {
        tagsCount: allTags?.length || 0,
        requestsCount: requests?.length || 0,
        userExists: !!user
      });
    }
  }, [customerId, allTags, requests, user, debug, onClose, onTagPicked, existingTags]);
  
  // Handle clicking a tag in the menu
  const handleTagClick = useCallback((tag) => (event) => {
    if (debug) log('[TagCustomersPicker] Tag clicked:', tag);
    
    // Call the onTagPicked callback immediately for optimistic UI update
    if (onTagPicked) {
      // This will update the UI immediately
      onTagPicked(tag);
    }
    
    // Close the picker immediately to improve UX
    handleClose(event);
    
    // Then add the tag to the customer through the API (in background)
    if (customerId) {
      if (debug) log('[TagCustomersPicker] Adding tag to customer:', { tagId: tag._id, customerId });
      
      // Start optimistic update by immediately updating any requests with this customer
      const updatedRequests = requests
        .filter(req => req.lookupTenant && req.lookupTenant._id === customerId)
        .map(req => {
          // Clone the request and its lookupTenant
          const updatedReq = { ...req };
          if (!updatedReq.lookupTenant) return updatedReq;
          
          // Add tag to the lookupTenant if it doesn't already exist
          const existingTagIds = updatedReq.lookupTenant.Tags || [];
          if (!existingTagIds.some(id => String(id) === String(tag._id))) {
            updatedReq.lookupTenant = {
              ...updatedReq.lookupTenant,
              Tags: [...existingTagIds, tag._id]
            };
          }
          
          return updatedReq;
        });
        
      if (updatedRequests.length > 0) {
        // Dispatch action to update requests in store (optimistic update)
        dispatch({
          type: UPDATE_REQUESTS,
          payload: updatedRequests
        });
        
        if (debug) log('[TagCustomersPicker] Optimistically updated requests:', updatedRequests.length);
      }
      
      // Then make the API call in the background
      try {
        addTagToCustomer({
          tagId: tag._id,
          customerIds: [customerId],
          user,
          showNotification: true
        })
        .unwrap()
        .then((result) => {
          if (debug) log('[TagCustomersPicker] Tag added successfully');
        })
        .catch((error) => {
          log('[TagCustomersPicker] Error adding tag:', error);
          
          // Revert optimistic update if API call fails
          dispatch(setSnackMessage({
            message: `Failed to add tag: ${error.message || 'Unknown error'}`,
            level: 'error',
            duration: 3000
          }));
          
          // TODO: Ideally we should revert the optimistic update here
        });
      } catch (error) {
        log('[TagCustomersPicker] Exception adding tag:', error);
      }
    }
  }, [debug, onTagPicked, customerId, addTagToCustomer, user, handleClose, dispatch, requests]);
  
  // Handle submission of a new tag
  const handleNewTagSubmit = useCallback(() => {
    if (!newTag.Name.trim()) return;
    
    if (debug) log('[TagCustomersPicker] Creating new tag:', newTag);
    
    // Close the picker immediately to improve UX
    handleClose();
    
    // Create a temporary tag with a local ID for optimistic UI update
    const tempTag = {
      _id: `temp_${Date.now()}`, // Temporary ID that will be replaced later
      Name: newTag.Name,
      Color: newTag.Color,
      name: newTag.Name, // Add both formats for compatibility
      color: newTag.Color
    };

    // Add temp tag to Redux tags store so TagRenderer can render it immediately
    dispatch({
      type: ADD_TAG,
      payload: { tag: tempTag }
    });

    // Call the onTagPicked callback immediately with the temp tag for optimistic UI update
    if (onTagPicked) {
      onTagPicked(tempTag);
    }
    
    // Start optimistic update by immediately updating any requests with this customer
    if (customerId) {
      const updatedRequests = requests
        .filter(req => req.lookupTenant && req.lookupTenant._id === customerId)
        .map(req => {
          // Clone the request and its lookupTenant
          const updatedReq = { ...req };
          if (!updatedReq.lookupTenant) return updatedReq;
          
          // Add temp tag to the lookupTenant
          const existingTagIds = updatedReq.lookupTenant.Tags || [];
          updatedReq.lookupTenant = {
            ...updatedReq.lookupTenant,
            Tags: [...existingTagIds, tempTag._id]
          };
          
          return updatedReq;
        });
        
      if (updatedRequests.length > 0) {
        // Dispatch action to update requests in store (optimistic update)
        dispatch({
          type: UPDATE_REQUESTS,
          payload: updatedRequests
        });
        
        if (debug) log('[TagCustomersPicker] Optimistically updated requests with temp tag:', updatedRequests.length);
      }
    }
    
    // Then call the createTag mutation via RTK Query and update with the real tag when it comes back
    createTagMutation({ name: newTag.Name, color: newTag.Color, user, showNotification: false })
      .unwrap()
      .then((result) => {
        const createdTag = result.tag;
        if (debug) log('[TagCustomersPicker] New tag created:', createdTag);

        // Remove temp tag from Redux tags store (createTag mutation already added the real tag)
        dispatch({
          type: DELETE_TAG,
          payload: { tag: tempTag }
        });

        // Replace the temporary tag with the real one in any requests
        if (customerId && createdTag) {
          const updatedRequestsWithRealTag = requests
            .filter(req => req.lookupTenant && req.lookupTenant._id === customerId)
            .map(req => {
              // Clone the request and its lookupTenant
              const updatedReq = { ...req };
              if (!updatedReq.lookupTenant) return updatedReq;

              // Replace temp tag with real tag
              const existingTagIds = updatedReq.lookupTenant.Tags || [];
              const updatedTags = existingTagIds
                .filter(id => id !== tempTag._id)  // Remove temp tag
                .concat([createdTag._id]);         // Add real tag

              updatedReq.lookupTenant = {
                ...updatedReq.lookupTenant,
                Tags: updatedTags
              };

              return updatedReq;
            });

          if (updatedRequestsWithRealTag.length > 0) {
            // Update requests with real tag ID
            dispatch({
              type: UPDATE_REQUESTS,
              payload: updatedRequestsWithRealTag
            });

            if (debug) log('[TagCustomersPicker] Updated requests with real tag ID:', updatedRequestsWithRealTag.length);
          }
        }

        // Add it to the customer in the backend
        if (customerId && createdTag) {
          addTagToCustomer({
            tagId: createdTag._id,
            customerIds: [customerId],
            user,
            showNotification: true
          })
          .unwrap()
          .then((result) => {
            if (debug) log('[TagCustomersPicker] New tag added to customer successfully');
          })
          .catch((error) => {
            log('[TagCustomersPicker] Error adding new tag to customer:', error);
            dispatch(setSnackMessage({
              message: `Failed to add tag: ${error.message || 'Unknown error'}`,
              level: 'error',
              duration: 3000
            }));
          });
        }
      })
      .catch((error) => {
        log('[TagCustomersPicker] Error creating tag:', error);
        dispatch(setSnackMessage({
          message: `Failed to create tag: ${error.message || 'Unknown error'}`,
          level: 'error',
          duration: 3000
        }));
      });
  }, [newTag, dispatch, customerId, onTagPicked, user, addTagToCustomer, createTagMutation, debug, handleClose, requests]);
  
  // Check if a tag is already selected for this customer
  const isTagSelected = useCallback((tagId) => {
    return existingTags.some(tag => tag._id === tagId);
  }, [existingTags]);
  
  // Handle color picking in the new tag form
  const handleColorPicked = useCallback((color) => {
    if (debug) log('[TagCustomersPicker] Color picked:', color);
  }, [debug]);
  
  // Define tag items based on the original styling
  const renderTagItems = () => {
    return allTags.map((tag) => (
      <MenuItem 
        key={tag._id}
        onClick={handleTagClick(tag)}
        disabled={isTagSelected(tag._id)}
        sx={{
          color: invert(tag.Color || tag.color, { black: '#080808', white: '#fafafa' }),
          backgroundColor: tag.Color || tag.color || '#ccc',
          '&:hover': {
            backgroundColor: tag.Color || tag.color || '#ccc',
            filter: 'brightness(0.95)'
          },
          padding: '6px 16px',
          fontSize: '0.875rem'
        }}
      >
        {tag.Name || tag.name}
      </MenuItem>
    ));
  };
  
  return (
    <Menu
      id="tag-picker-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      onClick={(e) => e.stopPropagation()}
      disablePortal={false}
      MenuListProps={{
        dense: true,
        sx: { 
          padding: '0',
          '& .MuiMenuItem-root': {
            minHeight: '40px'
          }
        }
      }}
      PaperProps={{
        elevation: 0,
        sx: { 
          minWidth: 220,
          maxHeight: 300,
          overflowY: 'auto',
          mt: 0.5,
          boxShadow: '0px 1px 4px rgba(0,0,0,0.2)',
          border: '1px solid #e0e0e0',
          borderRadius: '4px'
        }
      }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      keepMounted
    >
      {/* New tag input as a menu item */}
      <MenuItem 
        sx={{ 
          padding: '6px 8px',
          backgroundColor: '#f5f5f5',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          }
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <NewTagInput
          newTag={newTag}
          setNewTag={setNewTag}
          onSubmit={handleNewTagSubmit}
          onColorPicked={handleColorPicked}
          debug={debug}
        />
      </MenuItem>
      
      {/* List of existing tags */}
      {allTags && allTags.length > 0 ? renderTagItems() : (
        <MenuItem disabled sx={{ padding: '8px 16px' }}>
          No tags available
        </MenuItem>
      )}
    </Menu>
  );
};

export default TagCustomersPicker;